import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import type { MessageAttachedDocument } from '@/types/chat';
import { useAppSelector } from '@/store/hooks';
import { selectChatMatterInfo } from '@/store/slices/chat/chat';
import type { ChatPageLocationState } from './useAutoSendInitialChatPrompt';

interface UseChatPageParamsOptions {
  /** Matter id from the new-chat case mention. The loaded chat's matter takes priority. */
  caseMentionMatterId?: string;
}

export const useChatPageParams = ({ caseMentionMatterId }: UseChatPageParamsOptions = {}) => {
  const location = useLocation();
  const locationState = location.state as ChatPageLocationState | undefined;
  const chatMatterInfo = useAppSelector(selectChatMatterInfo);

  const caseId = chatMatterInfo?.matterId ?? caseMentionMatterId;
  const selectedFileIds = locationState?.selectedFileIds;
  const matterDocumentNamesById = locationState?.matterDocumentNamesById;

  const defaultSendOptions = useMemo(
    () =>
      caseId
        ? {
            context: {
              matterId: caseId,
              caseId,
              ...(selectedFileIds?.length ? { selectedCaseFileIds: selectedFileIds } : {}),
            },
          }
        : {},
    [caseId, selectedFileIds],
  );

  const resolveMatterQueryDocuments = useCallback(
    (documentIds: string[]): MessageAttachedDocument[] =>
      documentIds.map((documentId) => ({
        documentId,
        documentName: matterDocumentNamesById?.[documentId] ?? 'File',
      })),
    [matterDocumentNamesById],
  );

  return { defaultSendOptions, resolveMatterQueryDocuments };
};
